/**
 * 서버 고정 어휘(§19.3) → 화면 한국어. 한 곳에서만 정의한다 (리뷰 6회차 m30).
 *
 * 키는 services/common/aitex_common/providers_cloud.py `_VOCAB_FALLBACK` 과 같다
 * (정본은 DB vocabulary 테이블 — 새 값이 생기면 여기에 더한다).
 * 사전에 없는 값은 화면에 영문 원문으로 내보내지 않는다 — `koOr(v, null)` 로 숨기거나
 * 호출부가 「기타」 처럼 쓴다.
 */

export const COLOR_KO: Record<string, string> = {
  white: '흰색', black: '검정', gray: '회색', beige: '베이지', brown: '갈색',
  red: '빨강', orange: '주황', yellow: '노랑', green: '초록', blue: '파랑',
  purple: '보라', pink: '분홍',
};

export const PATTERN_KO: Record<string, string> = {
  solid: '무지', floral: '꽃무늬', geometric: '기하학', stripe: '스트라이프',
  check: '체크', dot: '도트', paisley: '페이즐리', animal: '애니멀',
  abstract: '추상', camouflage: '카무플라주', 'tie-dye': '타이다이',
  'jacquard-texture': '자카드 조직', other: '기타',
};

export const SCALE_KO: Record<string, string> = {
  micro: '아주 잔잔', small: '잔잔', medium: '보통', large: '큼', oversized: '아주 큼',
};

export const TEXTURE_KO: Record<string, string> = {
  brushed: '기모', coated: '코팅', crisp: '까슬함', fuzzy: '보송함', knit: '편물',
  rough: '거칢', slick: '미끈함', smooth: '매끄러움', soft: '부드러움', stiff: '빳빳함',
  stretchy: '신축성', woven: '직물',
};

export const STYLE_KO: Record<string, string> = {
  casual: '캐주얼', classic: '클래식', ethnic: '에스닉', industrial: '인더스트리얼',
  luxury: '고급스러움', minimal: '미니멀', modern: '모던', natural: '내추럴',
  retro: '레트로', romantic: '로맨틱', sporty: '스포티', vintage: '빈티지',
};

export const MOOD_KO: Record<string, string> = {
  bold: '강렬함', bright: '밝음', calm: '차분함', cool: '시원함', cozy: '포근함',
  dark: '어두움', elegant: '우아함', fresh: '산뜻함', playful: '발랄함', soft: '부드러움',
  vivid: '선명함', warm: '따뜻함',
};

export const USAGE_KO: Record<string, string> = {
  'apparel-bottom': '하의', 'apparel-outer': '아우터', 'apparel-top': '상의', bag: '가방',
  bedding: '침구', curtain: '커튼', cushion: '쿠션', other: '기타', shoe: '신발',
  sleepwear: '잠옷', sofa: '소파', tablecloth: '테이블보',
};

export const SHEEN_KO: Record<string, string> = {
  glossy: '유광', matte: '무광', metallic: '메탈릭', 'semi-gloss': '반광',
};

export const OPACITY_KO: Record<string, string> = {
  opaque: '불투명', 'semi-sheer': '반투명', sheer: '투명',
};

/** 기본정보의 코드값 — 솔리드/나염 · 나염 방식 */
export const PRINT_KO: Record<string, string> = {
  // 서버 편집 API(2026-10-18)는 선염을 'yarn-dyed' 로 쓴다 — 두 표기 모두
  printed: '나염', solid: '솔리드', yarn_dyed: '선염', 'yarn-dyed': '선염', unknown: '미확인',
};

/** 섬유 (계획서 U4) — 서버 목록 material= 값 · facets.material (services/api/app/routers/fabric.py FIBERS) */
export const FIBER_KO: Record<string, string> = {
  polyester: '폴리에스터', spandex: '스판덱스', nylon: '나일론', cotton: '면', rayon: '레이온',
  wool: '울', linen: '린넨', acrylic: '아크릴', metallic: '메탈릭',
};
export const FIBERS = Object.keys(FIBER_KO);

/** 질의 해석 · 패싯의 축 이름 */
export const FIELD_KO: Record<string, string> = {
  color: '색상', dominant_color: '색상', pattern: '패턴', pattern_scale: '패턴 크기',
  mood: '분위기', style: '스타일', usage: '용도', texture: '질감', sheen: '광택',
  opacity: '비침', manufacturer: '제조사', material: '소재',
};

/** 축 → 그 축의 값 사전 */
export const VALUE_BY_FIELD: Record<string, Record<string, string>> = {
  color: COLOR_KO, dominant_color: COLOR_KO, pattern: PATTERN_KO, pattern_scale: SCALE_KO,
  mood: MOOD_KO, style: STYLE_KO, usage: USAGE_KO, texture: TEXTURE_KO,
  sheen: SHEEN_KO, opacity: OPACITY_KO, print: PRINT_KO,
};

/** 어느 축인지 모를 때(상세의 칩 묶음) — 축 사전을 차례로 찾는다. 같은 영문이 축마다 뜻이 같다. */
const ANY: Record<string, string> = {
  ...PRINT_KO, ...COLOR_KO, ...PATTERN_KO, ...SCALE_KO, ...TEXTURE_KO, ...STYLE_KO, ...MOOD_KO,
  ...USAGE_KO, ...SHEEN_KO, ...OPACITY_KO,
};

/** 축을 알면 그 축 사전, 모르면 전체 사전. 없으면 fallback(기본: 원문). */
export function ko(v: string, field?: string, fallback?: string | null): string | null {
  const dict = field ? VALUE_BY_FIELD[field] : undefined;
  const hit = dict?.[v] ?? ANY[v];
  if (hit) return hit;
  return fallback === undefined ? v : fallback;
}

/** 서버 원문을 절대 그대로 보이지 않는 화면용 — 사전에 없으면 「기타」. */
export function koOrOther(v: string, field?: string): string {
  return ko(v, field, '기타') as string;
}
